import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { pedirTextoIA } from '../utils/aiClient';
import { useApp } from '../context/AppContext';
import {
  TrendingUp, TrendingDown, Users, DollarSign, Sparkles, Award,
  RefreshCw, Target, BarChart2, AlertCircle, FileText,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { parseISO, differenceInDays, addMonths } from 'date-fns';
import { fmtPatron } from '../utils/dates';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const fmtMoney = (n) => '$ ' + Number(n || 0).toLocaleString('es-UY', { maximumFractionDigits: 0 });
const monthKey = (d) => (d || '').slice(0, 7);

// Agrupa un motivo de cancelación en texto libre a una CATEGORÍA canónica,
// para que "NO RENOVO", "no renueva", "no quiso renovar" caigan todos juntos.
// El orden importa: se evalúa de la más específica a la más general.
function categorizarMotivo(textoRaw) {
  const t = (textoRaw || '').toLowerCase().trim();
  if (!t) return 'Sin motivo';
  const has = (...keys) => keys.some(k => t.includes(k));
  // Falta de pago / deuda / anulación por mora
  if (has('falta de pago', 'no pag', 'impag', 'deuda', 'mora', 'anulac')) return 'Falta de pago / deuda';
  // Venta del bien (auto vendido, etc.)
  if (has('vendi', 'venta', 'vendio', 'transfir', 'cambio de titular', 'cambió de titular', 'cambio titular')) return 'Venta / cambio de titular';
  // Cambio de corredor / se fue a otro
  if (has('cambio corredor', 'cambio de corredor', 'cambió de corredor', 'otro corredor', 'otra corredora', 'se fue', 'cambio productor')) return 'Cambio de corredor';
  // Hizo seguro nuevo en otra compañía / se fue a la competencia
  if (has('hizo seg', 'seguro nuevo', 'otra comp', 'competencia', 'mejor precio', 'mas barato', 'más barato')) return 'Se fue a la competencia';
  // Siniestro total / robo / destrucción del bien
  if (has('hurto', 'robo', 'siniestro total', 'destruccion', 'destrucción', 'perdida total', 'pérdida total', 'choque total')) return 'Siniestro / hurto del bien';
  // No contesta / sin respuesta
  if (has('no contest', 'no respond', 'no atiende', 'sin respuesta', 'no me contestaron', 'ilocalizable')) return 'Sin respuesta del cliente';
  // No quiso / decidió no renovar
  if (has('no renov', 'no renue', 'no quiso renov', 'decidio no', 'decidió no', 'no continua', 'no continúa', 'da de baja', 'baja')) return 'No renovó / dio de baja';
  // Fallecimiento
  if (has('fallec', 'falleci', 'defuncion', 'defunción', 'murio', 'murió')) return 'Fallecimiento';
  // Mudanza / se fue del país
  if (has('mudanza', 'se mudo', 'se mudó', 'emigr', 'se fue del pais', 'exterior')) return 'Mudanza / emigró';
  return 'Otros';
}


const TABS = [
  { id: 'retencion', label: 'Retención y fuga', icon: TrendingUp },
  { id: 'equipo', label: 'Equipo', icon: Award },
  { id: 'proyeccion', label: 'Proyección', icon: DollarSign },
  { id: 'crosssell', label: 'Cross-sell', icon: Target },
  { id: 'resumen', label: 'Resumen ejecutivo', icon: Sparkles },
];

export default function InsightsPage() {
  const { currentUser } = useApp();
  const [tab, setTab] = useState('retencion');

  if (currentUser?.role !== 'admin') {
    return (
      <div className="page fade-in">
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <BarChart2 size={32} color="var(--text-muted)" style={{ marginBottom: 12 }} />
          <h2>Acceso restringido</h2>
          <p style={{ color: 'var(--text-secondary)' }}>Inteligencia de Negocio está disponible solo para el CEO / administrador.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title"><BarChart2 size={22} style={{ display: 'inline', marginRight: 8, verticalAlign: '-3px', color: 'var(--accent)' }} />Inteligencia de Negocio</h1>
          <p className="page-subtitle">La lectura de negocio de tu cartera · solo CEO</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.id)}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'retencion' && <Retencion />}
      {tab === 'equipo' && <Equipo />}
      {tab === 'proyeccion' && <Proyeccion />}
      {tab === 'crosssell' && <CrossSell />}
      {tab === 'resumen' && <ResumenEjecutivo />}
    </div>
  );
}

// Helpers compartidos ────────────────────────────────────────────────────────
function authHeaders() {
  const token = localStorage.getItem('nebb_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function StatCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="stat-card" style={{ borderLeft: `3px solid ${color}` }}>
      <div className="stat-label">{Icon && <Icon size={11} style={{ display: 'inline', marginRight: 5 }} />}{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

// ── 1. RETENCIÓN Y FUGA ──────────────────────────────────────────────────────
function Retencion() {
  const { policies, companies } = useApp();
  const today = new Date();

  // Renovaciones y cancelaciones leídas del historial de cada póliza (últimos 12 meses)
  const data = useMemo(() => {
    const hace12 = addMonths(today, -12);
    let renovadas = 0, perdidas = 0, primaRenovada = 0, primaPerdida = 0;
    const porMes = {}; // 'YYYY-MM' -> { renov, perd }
    const motivosFuga = {};
    const commissionOf = (p) => {
      const co = companies.find(c => c.name === p.company);
      return (p.premium || 0) * ((co?.commissionRate || 0) / 100);
    };
    policies.forEach(p => {
      (p.history || []).forEach(h => {
        const fecha = h.date ? parseISO(h.date) : null;
        if (!fecha || fecha < hace12) return;
        const mk = monthKey(h.date);
        if (h.type === 'renovacion') {
          renovadas++; primaRenovada += (p.premium || 0);
          porMes[mk] = porMes[mk] || { renov: 0, perd: 0 };
          porMes[mk].renov++;
        } else if (h.type === 'cancelacion') {
          perdidas++; primaPerdida += (p.premium || 0);
          porMes[mk] = porMes[mk] || { renov: 0, perd: 0 };
          porMes[mk].perd++;
          const motivoRaw = (h.desc || 'Sin motivo').trim();
          const cat = categorizarMotivo(motivoRaw);
          if (!motivosFuga[cat]) motivosFuga[cat] = { count: 0, ejemplos: {} };
          motivosFuga[cat].count++;
          // Guardamos los textos originales que cayeron en esta categoría (para detalle)
          const ej = motivoRaw.slice(0, 50);
          motivosFuga[cat].ejemplos[ej] = (motivosFuga[cat].ejemplos[ej] || 0) + 1;
        }
      });
    });
    const totalGestion = renovadas + perdidas;
    const tasaRetencion = totalGestion > 0 ? Math.round((renovadas / totalGestion) * 100) : 0;
    // serie de los últimos 6 meses
    const serie = [];
    for (let i = 5; i >= 0; i--) {
      const d = addMonths(today, -i);
      const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const v = porMes[mk] || { renov: 0, perd: 0 };
      serie.push({ name: `${MESES[d.getMonth()]}`, Renovadas: v.renov, Perdidas: v.perd });
    }
    const motivos = Object.entries(motivosFuga).map(([k, v]) => ({
      name: k,
      value: v.count,
      ejemplos: Object.entries(v.ejemplos).sort((a, b) => b[1] - a[1]).map(([txt, n]) => ({ txt, n })),
    })).sort((a, b) => b.value - a.value);
    return { renovadas, perdidas, tasaRetencion, primaRenovada, primaPerdida, serie, motivos };
  }, [policies, companies]);

  return (
    <div className="fade-in">
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard label="Tasa de retención (12m)" value={`${data.tasaRetencion}%`} sub={`${data.renovadas} renovadas / ${data.perdidas} perdidas`} color="var(--accent)" icon={TrendingUp} />
        <StatCard label="Pólizas renovadas" value={data.renovadas} sub="últimos 12 meses" color="var(--blue)" icon={RefreshCw} />
        <StatCard label="Pólizas perdidas" value={data.perdidas} sub="últimos 12 meses" color="var(--red)" icon={TrendingDown} />
        <StatCard label="Prima perdida" value={fmtMoney(data.primaPerdida)} sub="que se fue de cartera" color="var(--red)" icon={DollarSign} />
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 14 }}>Renovadas vs perdidas (últimos 6 meses)</h3>
        {data.serie.some(s => s.Renovadas || s.Perdidas) ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.serie}>
              <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} />
              <YAxis stroke="var(--text-muted)" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
              <Bar dataKey="Renovadas" fill="var(--accent)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Perdidas" fill="var(--red)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyMsg text="Todavía no hay movimientos de renovación o cancelación registrados en los últimos meses." />}
      </div>

      <div className="card">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 4 }}>Motivos de fuga</h3>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>Agrupados por categoría. Tocá una fila para ver los textos originales que la componen.</p>
        {data.motivos.length > 0 ? (
          <MotivosLista motivos={data.motivos} total={data.perdidas} />
        ) : <EmptyMsg text="No hay cancelaciones registradas." />}
      </div>
    </div>
  );
}

// ── 2. EQUIPO ────────────────────────────────────────────────────────────────
function Equipo() {
  const [renewals, setRenewals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandida, setExpandida] = useState(null);   // persona cuyas fichas se ven
  const [sel, setSel] = useState(new Set());          // ids de fichas tildadas
  const [destino, setDestino] = useState('');         // a quién asignar
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const [rRes, uRes] = await Promise.all([
        fetch('/api/renewals', { headers: authHeaders() }),
        fetch('/api/users', { headers: authHeaders() }),
      ]);
      const rData = await rRes.json();
      const uData = await uRes.json();
      setRenewals(Array.isArray(rData) ? rData : []);
      setUsers(Array.isArray(uData) ? uData : []);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, []);

  // Nombres asignables (los del equipo). Se usa el name del usuario.
  const asignables = useMemo(
    () => users.map(u => u.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [users]
  );

  const porPersona = useMemo(() => {
    const map = {};
    renewals.forEach(r => {
      const persona = r.assignedTo || 'Sin asignar';
      map[persona] = map[persona] || { asignadas: 0, gestionadas: 0, retenidas: 0, perdidas: 0 };
      map[persona].asignadas++;
      if (r.outcome) {
        map[persona].gestionadas++;
        if (r.outcome.startsWith('renueva') || r.outcome === 'endoso') map[persona].retenidas++;
        else if (r.outcome.startsWith('cancel')) map[persona].perdidas++;
      }
    });
    return Object.entries(map).map(([nombre, v]) => ({
      nombre, ...v,
      tasa: v.gestionadas > 0 ? Math.round((v.retenidas / v.gestionadas) * 100) : null,
    })).sort((a, b) => b.asignadas - a.asignadas);
  }, [renewals]);

  // Fichas de la persona expandida
  const fichasExpandida = useMemo(() => {
    if (expandida === null) return [];
    return renewals
      .filter(r => (r.assignedTo || 'Sin asignar') === expandida)
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  }, [renewals, expandida]);

  const toggleExpandir = (nombre) => {
    setExpandida(prev => (prev === nombre ? null : nombre));
    setSel(new Set());
  };

  const toggleSel = (id) => {
    setSel(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleTodas = () => {
    setSel(prev => {
      if (prev.size === fichasExpandida.length) return new Set();
      return new Set(fichasExpandida.map(r => r.id));
    });
  };

  const asignar = async () => {
    if (sel.size === 0) return;
    setGuardando(true);
    try {
      const ids = [...sel];
      const target = destino === '__none__' ? '' : destino;
      const res = await fetch('/api/renewals/bulk-assign', {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ ids, assignedTo: target }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      // Actualizamos en memoria sin recargar todo
      setRenewals(rs => rs.map(r => (sel.has(r.id) ? { ...r, assignedTo: target } : r)));
      setSel(new Set());
      setDestino('');
    } catch { /* noop */ }
    setGuardando(false);
  };

  if (loading) return <EmptyMsg text="Cargando datos del equipo..." />;

  return (
    <div className="fade-in">
      <div className="card">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 14 }}>Performance en renovaciones por persona</h3>
        {porPersona.length > 0 ? (
          <div className="table-wrap table-scroll">
            <table>
              <thead><tr><th>Responsable</th><th>Asignadas</th><th>Gestionadas</th><th>Retenidas</th><th>Perdidas</th><th>Tasa retención</th></tr></thead>
              <tbody>
                {porPersona.map((p, i) => (
                  <tr
                    key={i}
                    style={{ cursor: 'pointer', background: expandida === p.nombre ? 'var(--accent-dim)' : undefined }}
                    onClick={() => toggleExpandir(p.nombre)}
                  >
                    <td>
                      <strong>{p.nombre}</strong>
                      <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--accent)' }}>
                        {expandida === p.nombre ? '▾ ocultar fichas' : '▸ ver fichas'}
                      </span>
                    </td>
                    <td>{p.asignadas}</td>
                    <td>{p.gestionadas}</td>
                    <td style={{ color: 'var(--accent)' }}>{p.retenidas}</td>
                    <td style={{ color: 'var(--red)' }}>{p.perdidas}</td>
                    <td>
                      {p.tasa === null ? <span style={{ color: 'var(--text-muted)' }}>—</span> : (
                        <span className="badge" style={{ background: p.tasa >= 70 ? 'var(--accent-dim)' : p.tasa >= 40 ? 'var(--amber-dim)' : 'var(--red-dim)', color: p.tasa >= 70 ? 'var(--accent)' : p.tasa >= 40 ? 'var(--amber)' : 'var(--red)' }}>{p.tasa}%</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyMsg text="Todavía no hay renovaciones asignadas para medir performance." />}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          La performance se mide sobre las renovaciones gestionadas: cuántas el responsable logró retener (renovar/endosar) vs. cuántas se cancelaron.
        </p>
      </div>

      {/* Detalle de fichas de la persona expandida + asignación masiva */}
      {expandida !== null && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 4 }}>
            Fichas de {expandida} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({fichasExpandida.length})</span>
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Tildá las que quieras mover y elegí el responsable. Podés seleccionar varias y asignarlas de una sola vez.
          </p>

          {/* Barra de acción masiva */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: sel.size ? 'var(--accent)' : 'var(--text-muted)' }}>
              {sel.size} seleccionada{sel.size !== 1 ? 's' : ''}
            </span>
            <select
              value={destino}
              onChange={e => setDestino(e.target.value)}
              style={{ flex: '1 1 160px', minWidth: 140, maxWidth: 220 }}
              disabled={sel.size === 0}
            >
              <option value="">— Asignar a… —</option>
              {asignables.map(n => <option key={n} value={n}>{n}</option>)}
              <option value="__none__">Sin asignar</option>
            </select>
            <button
              className="btn btn-primary btn-sm"
              disabled={sel.size === 0 || destino === '' || guardando}
              onClick={asignar}
            >
              {guardando ? 'Asignando...' : `Asignar ${sel.size || ''}`.trim()}
            </button>
          </div>

          {fichasExpandida.length === 0 ? (
            <EmptyMsg text="Esta persona no tiene fichas." />
          ) : (
            <div className="table-wrap table-scroll">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={sel.size === fichasExpandida.length && fichasExpandida.length > 0}
                        onChange={toggleTodas}
                      />
                    </th>
                    <th>Cliente</th>
                    <th>Póliza</th>
                    <th>Compañía</th>
                    <th>Vence</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {fichasExpandida.map(r => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => toggleSel(r.id)}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={sel.has(r.id)} onChange={() => toggleSel(r.id)} />
                      </td>
                      <td style={{ fontSize: 13 }}>{r.clientName || '(sin ficha)'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{r.policyNumber || '—'}</td>
                      <td style={{ fontSize: 12.5 }}>{r.company || '—'}</td>
                      <td style={{ fontSize: 12.5 }}>{r.dueDate ? fmtPatron(r.dueDate, 'dd/MM/yyyy') : '—'}</td>
                      <td style={{ fontSize: 12 }}>{r.outcome ? 'Finalizada' : (r.status === 'in_progress' ? 'En proceso' : 'Pendiente')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 3. PROYECCIÓN DE COMISIONES ──────────────────────────────────────────────
function Proyeccion() {
  const { policies, companies } = useApp();
  const today = new Date();
  const [retencion, setRetencion] = useState(85); // escenario ajustable

  const data = useMemo(() => {
    const commissionOf = (p) => {
      const co = companies.find(c => c.name === p.company);
      return (p.premium || 0) * ((co?.commissionRate || 0) / 100);
    };
    const active = policies.filter(p => p.status === 'active' || p.status === 'endorsed');
    const serie = [];
    for (let i = 0; i < 6; i++) {
      const d = addMonths(today, i);
      const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      // pólizas que vencen ese mes → comisión potencial a renovar
      const venc = active.filter(p => monthKey(p.endDate) === mk);
      const comisionBruta = venc.reduce((acc, p) => acc + commissionOf(p), 0);
      serie.push({
        name: `${MESES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
        polizas: venc.length,
        bruta: Math.round(comisionBruta),
        estimada: Math.round(comisionBruta * (retencion / 100)),
      });
    }
    const totalEstimado = serie.reduce((acc, s) => acc + s.estimada, 0);
    return { serie, totalEstimado };
  }, [policies, companies, retencion]);

  return (
    <div className="fade-in">
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, margin: 0 }}>Comisiones proyectadas (próximos 6 meses)</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Escenario de retención:</span>
            <select value={retencion} onChange={e => setRetencion(Number(e.target.value))}
              style={{ padding: '6px 10px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13 }}>
              <option value={100}>100% (todas renuevan)</option>
              <option value={90}>90%</option>
              <option value={85}>85%</option>
              <option value={70}>70%</option>
              <option value={50}>50%</option>
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 16, padding: 14, background: 'var(--accent-dim)', borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Comisión estimada total (6 meses, al {retencion}%): </span>
          <strong style={{ fontSize: 18, color: 'var(--accent)' }}>{fmtMoney(data.totalEstimado)}</strong>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.serie}>
            <XAxis dataKey="name" stroke="var(--text-muted)" fontSize={12} />
            <YAxis stroke="var(--text-muted)" fontSize={11} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} formatter={v => fmtMoney(v)} />
            <Bar dataKey="estimada" fill="var(--accent)" radius={[4, 4, 0, 0]} name="Comisión estimada" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Mes</th><th>Pólizas que vencen</th><th>Comisión potencial</th><th>Estimada ({retencion}%)</th></tr></thead>
            <tbody>
              {data.serie.map((s, i) => (
                <tr key={i}><td>{s.name}</td><td>{s.polizas}</td><td style={{ color: 'var(--text-secondary)' }}>{fmtMoney(s.bruta)}</td><td style={{ color: 'var(--accent)' }}>{fmtMoney(s.estimada)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          La comisión usa la tasa configurada en cada compañía. Si una compañía no tiene tasa cargada, su comisión cuenta como 0 — cargá las tasas en Compañías para mayor precisión.
        </p>
      </div>
    </div>
  );
}

// ── 4. CROSS-SELL ────────────────────────────────────────────────────────────
function CrossSell() {
  const { clients, policies } = useApp();
  const navigate = useNavigate();

  const data = useMemo(() => {
    const active = policies.filter(p => p.status !== 'cancelled');
    const porCliente = {};
    active.forEach(p => {
      if (!p.clientId) return;
      porCliente[p.clientId] = porCliente[p.clientId] || [];
      porCliente[p.clientId].push(p);
    });
    // Clientes con UNA sola póliza → oportunidad de segunda venta
    const unaPoliza = [];
    Object.entries(porCliente).forEach(([cid, pols]) => {
      if (pols.length === 1) {
        const cli = clients.find(c => c.id === cid);
        if (cli) {
          const tipo = pols[0].type || '';
          // sugerencia simple según lo que ya tiene
          const sug = tipo.toLowerCase().includes('auto') ? 'Hogar / Vida' : tipo.toLowerCase().includes('hogar') ? 'Auto / Vida' : 'Auto / Hogar';
          unaPoliza.push({ id: cid, name: cli.name, tiene: tipo, sugerencia: sug });
        }
      }
    });
    return { unaPoliza: unaPoliza.sort((a, b) => a.name.localeCompare(b.name)) };
  }, [clients, policies]);

  return (
    <div className="fade-in">
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <StatCard label="Clientes con 1 sola póliza" value={data.unaPoliza.length} sub="oportunidad de segunda venta" color="var(--blue)" icon={Target} />
      </div>
      <div className="card">
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, marginBottom: 14 }}>Oportunidades de cross-sell</h3>
        {data.unaPoliza.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Cliente</th><th>Tiene</th><th>Podría sumar</th><th></th></tr></thead>
              <tbody>
                {data.unaPoliza.slice(0, 100).map((c, i) => (
                  <tr key={i}>
                    <td><strong>{c.name}</strong></td>
                    <td><span className="badge">{c.tiene || '—'}</span></td>
                    <td style={{ color: 'var(--accent)' }}>{c.sugerencia}</td>
                    <td><button className="btn btn-secondary btn-sm" onClick={() => navigate(`/clients/${c.id}`)}>Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.unaPoliza.length > 100 && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>Mostrando los primeros 100 de {data.unaPoliza.length}.</p>}
          </div>
        ) : <EmptyMsg text="Todos tus clientes tienen más de una póliza, o no hay datos suficientes." />}
      </div>
    </div>
  );
}

// ── 5. RESUMEN EJECUTIVO (IA) ────────────────────────────────────────────────
function ResumenEjecutivo() {
  const { policies, clients, companies } = useApp();
  const today = new Date();
  const [loading, setLoading] = useState(false);
  const [texto, setTexto] = useState('');
  const [generadoEl, setGeneradoEl] = useState('');
  const [error, setError] = useState('');

  // Rehidratar el último resumen guardado al abrir la pestaña.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/exec-summary', { headers: authHeaders() });
        const data = await r.json();
        if (data && data.text) { setTexto(data.text); setGeneradoEl(data.createdAt || ''); }
      } catch { /* noop */ }
    })();
  }, []);

  const generar = async () => {
    setLoading(true); setError(''); setTexto('');
    try {
      // Armamos un set de métricas reales (sin datos personales sensibles)
      const active = policies.filter(p => p.status === 'active' || p.status === 'endorsed');
      const cancelled = policies.filter(p => p.status === 'cancelled');
      const pend = active.filter(p => p.fromRenewalNotice).length;
      const mkNow = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
      const vencenEsteMes = active.filter(p => monthKey(p.endDate) === mkNow).length;
      const porCia = {};
      active.forEach(p => { const c = (p.company || 'Sin compañía'); porCia[c] = (porCia[c] || 0) + 1; });
      const topCias = Object.entries(porCia).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n, c]) => `${n}: ${c}`).join(', ');

      const metricas = {
        clientes: clients.length,
        polizasActivas: active.length,
        polizasCanceladas: cancelled.length,
        renovacionesPendientes: pend,
        vencenEsteMes,
        topCompanias: topCias,
        mes: `${MESES[today.getMonth()]} ${today.getFullYear()}`,
      };

      const prompt = `Sos un analista de negocio. Con estas métricas de una corredora de seguros uruguaya (NEBB Brokers), escribí un RESUMEN EJECUTIVO breve para el CEO, en español rioplatense, en prosa, de no más de 200 palabras. Destacá lo importante, señalá riesgos u oportunidades y dale 2 o 3 recomendaciones accionables. No inventes datos que no estén acá. No uses markdown ni viñetas, solo párrafos.

Métricas (${metricas.mes}):
- Clientes en cartera: ${metricas.clientes}
- Pólizas activas: ${metricas.polizasActivas}
- Pólizas canceladas (histórico): ${metricas.polizasCanceladas}
- Renovaciones pendientes de gestionar: ${metricas.renovacionesPendientes}
- Pólizas que vencen este mes: ${metricas.vencenEsteMes}
- Principales compañías por volumen: ${metricas.topCompanias}`;

      const { texto: t } = await pedirTextoIA({ content: prompt, model: 'claude-sonnet-4-5', maxTokens: 1500 });
      const final = t || 'No se pudo generar el resumen.';
      setTexto(final);
      // Persistir para que sobreviva a recargas.
      try {
        await fetch('/api/exec-summary', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text: final, metrics }) });
        setGeneradoEl(new Date().toISOString());
      } catch { /* noop */ }
    } catch (e) {
      setError(e.message || 'Error al generar el resumen.');
    }
    setLoading(false);
  };

  return (
    <div className="fade-in">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, margin: 0 }}>Resumen ejecutivo del negocio</h3>
          <button className="btn btn-primary btn-sm" onClick={generar} disabled={loading}>
            {loading ? <><span className="spinner" style={{ display: 'inline-block', width: 13, height: 13, border: '2px solid rgba(0,0,0,0.2)', borderTopColor: '#fff', borderRadius: '50%' }} /> Generando...</> : <><Sparkles size={14} /> Generar resumen</>}
          </button>
        </div>
        {error && <div style={{ padding: '10px 14px', background: 'var(--red-dim)', color: 'var(--red)', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        {texto ? (
          <div>
            {generadoEl && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Generado el {fmtPatron(generadoEl, 'dd/MM/yyyy HH:mm')} · se conserva hasta que generes uno nuevo</p>}
            <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{texto}</div>
          </div>
        ) : !loading && (
          <EmptyMsg text="Tocá 'Generar resumen' para que la IA te dé una lectura del estado del negocio con recomendaciones." />
        )}
      </div>
    </div>
  );
}

function MotivosLista({ motivos, total }) {
  const [abierto, setAbierto] = useState(null);
  const max = Math.max(...motivos.map(m => m.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {motivos.map((m, i) => {
        const pct = total > 0 ? Math.round((m.value / total) * 100) : 0;
        const open = abierto === i;
        const tieneDetalle = m.ejemplos && m.ejemplos.length > 0;
        return (
          <div key={i} style={{ borderBottom: '1px solid var(--border)' }}>
            <div onClick={() => tieneDetalle && setAbierto(open ? null : i)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: tieneDetalle ? 'pointer' : 'default' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{tieneDetalle && <span style={{ color: 'var(--text-muted)', marginRight: 6, fontSize: 11 }}>{open ? '▼' : '▶'}</span>}{m.name}</span>
                  <span style={{ fontSize: 13 }}><strong style={{ color: 'var(--red)' }}>{m.value}</strong> <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>({pct}%)</span></span>
                </div>
                <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${(m.value / max) * 100}%`, height: '100%', background: 'var(--red)', opacity: 0.7, borderRadius: 3 }} />
                </div>
              </div>
            </div>
            {open && tieneDetalle && (
              <div style={{ padding: '4px 0 12px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {m.ejemplos.map((e, j) => (
                  <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span>· {e.txt}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{e.n}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EmptyMsg({ text }) {
  return (
    <div style={{ textAlign: 'center', padding: 28, color: 'var(--text-muted)' }}>
      <FileText size={24} style={{ marginBottom: 8 }} />
      <p style={{ fontSize: 13 }}>{text}</p>
    </div>
  );
}